import { useState } from "react";
import { useTheme } from "../../styles/ThemeProvider";

// Theme-aware avatar colors that follow WAC standards
const getAvatarColors = (mode: 'light' | 'dark') => {
  if (mode === 'dark') {
    return [
      'var(--color-primary)',     // #1581a7
      'var(--color-secondary)',   // #c084fc
      'var(--color-success)',     // #4ade80
      'var(--color-warning)',     // #fbbf24
      'var(--color-error)',       // #f87171
      'var(--color-info)',        // #38bdf8
      '#7c3aed',                  // purple-600 (good contrast)
      '#db2777',                  // pink-600 (good contrast)
    ];
  } else {
    return [
      'var(--color-primary)',     // #1581a7
      'var(--color-secondary)',   // #9333ea
      'var(--color-success)',     // #16a34a
      'var(--color-warning)',     // #f59e0b
      'var(--color-error)',       // #dc2626
      'var(--color-info)',        // #0ea5e9
      '#4f46e5',                  // indigo-600 (good contrast)
      '#059669',                  // emerald-600 (good contrast)
    ];
  }
};

interface AvatarProps {
  username: string;
  /** A picture from a linked Google or Facebook account, if the reader wants one shown. */
  src?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export function Avatar({ username, src, size = 'md', className = '' }: AvatarProps) {
  const { mode } = useTheme();
  // A provider's picture URL can expire or be blocked; initials are always
  // available, so a broken image falls back rather than leaving a gap.
  const [failed, setFailed] = useState(false);

  const avatarColors = getAvatarColors(mode);

  // Derive a stable background colour from the username so initials always have the same colour.
  const avatarColour = (username: string): string => {
    let hash = 0;
    for (let i = 0; i < username.length; i++) {
      hash = username.charCodeAt(i) + ((hash << 5) - hash);
    }
    return avatarColors[Math.abs(hash) % avatarColors.length];
  };

  const initials = username.charAt(0).toUpperCase();
  const bgColour = avatarColour(username);

  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-9 h-9 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-20 h-20 text-2xl',
  };

  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className={`rounded-full object-cover select-none shadow-sm ${sizeClasses[size]} ${className}`}
      />
    );
  }

  return (
    <span
      className={`rounded-full flex items-center justify-center text-white font-bold select-none shadow-sm ${sizeClasses[size]} ${className}`}
      style={{ backgroundColor: bgColour }}
    >
      {initials}
    </span>
  );
}